import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import type { PortalDog, PortalMe } from '@bbbk/schema';
import { Avatar } from '../components/common';
import { G } from './glyphs';
import { Tag } from './Tag';
import { initialOf } from './RegistrationDetails';
import { WhatsAppMark } from './MessagePatricia';
import { whatsappHref } from './whatsapp';
import { PrimarySubmit, StepBar, StepFailure, StepPage, type StepError } from './RegistrationChrome';
import {
  datesOnNewCard,
  hasCard,
  dogLine,
  fileProblem,
  fileSize,
  maskComplete,
  maskMonthYear,
  maskToVaccineDue,
  monthYearLabel,
  vaccineDueToMask,
  vaxState,
  type VaxMark,
} from './registrationRules';

// `c2d` / `c2dv` / `c2e` — Your dogs, step 2 of 2 (AC16–AC29).
//
// One card per dog, the first open and the rest folded to a row. Name and
// breed are the desk's and are shown, not edited (decisions 82, 157);
// medication and *with other dogs* are the customer's to say. Vaccinations is
// its own panel: the card as a file, and the two dates — editable only once a
// card is attached, because a date without a card is a claim with no evidence
// (AC25). No *Add a dog*: a new dog is Patricia's to add (decision 157), so
// the foot of the list is the same line the Dogs tab carries.

export interface DogAnswer {
  /** The dog's name, prefilled from the desk and the customer's to correct. */
  name: string;
  /** The breed, same — blank is allowed, a nameless dog is not. */
  breed: string;
  meds: 'none' | 'yes';
  instructions: string;
  apart: 'mixes' | 'apart';
  /** A card chosen on this phone, not yet sent. */
  file: File | null;
  /** Why the last chosen file was refused before sending (AC22). */
  fileError: string | null;
  /** `MM/YY` as typed; '' for none. */
  validUntil: string;
  kcUntil: string;
}

export type DogAnswers = Record<string, DogAnswer>;

/** What the step starts from — the desk's facts, and the card already on file. */
export function blankAnswers(me: PortalMe): DogAnswers {
  const out: DogAnswers = {};
  for (const d of me.dogs) {
    out[d.id] = {
      name: d.name,
      breed: d.breed ?? '',
      meds: d.meds ? 'yes' : 'none',
      instructions: d.meds ?? '',
      // THE DOG'S OWN ANSWER FIRST (PR #231 review, items 20, 21). A resumed
      // step or a second contact starts from what was already said about this
      // dog; the household flag speaks only where nobody has, and a customer
      // submitting it unchanged is then confirming it, not being handed it.
      apart: d.keepApart === true ? 'apart' : d.keepApart === false ? 'mixes' : me.doNotMix ? 'apart' : 'mixes',
      file: null,
      fileError: null,
      // PATRICIA HAS ONE FIELD; THE CARD HAS TWO (Freddi, 2026-09-19: "if
      // patricia enters jun 26 against a dog she has on her side that should
      // appear in both vaccination fields on the customers registration form").
      //
      // The household modal draws *Vaccinations due* and nothing else — there
      // is no kennel-cough box on her side, and `dogs.kc_due` is a column only
      // the customer's own answers ever fill. So her single date is the whole
      // of what we hold about this dog's vaccinations, and it belongs in both
      // fields: leaving the second empty tells the customer we know less than
      // we do, and invites them to retype the date they can see above it.
      //
      // It is a starting point, not an assertion. They are looking at the card
      // while they read it, both fields are editable, and whichever is wrong
      // they correct — which is the entire point of asking them.
      //
      // A CARD ON FILE SPEAKS ONLY FOR ITSELF (P2.25, 2026-09-15). Where one
      // exists the desk's typing is not merged into it in either direction: a
      // stored card that claimed no kennel cough claims none, because the two
      // are deliberately separate sources and the receipt reads the proof.
      ...(d.card.state === 'added'
        ? {
            validUntil: vaccineDueToMask(d.card.validUntil),
            kcUntil: vaccineDueToMask(d.card.kcUntil),
          }
        : {
            validUntil: vaccineDueToMask(d.vaccinesDue),
            kcUntil: vaccineDueToMask(d.kcDue ?? d.vaccinesDue),
          }),
    };
  }
  return out;
}

/** The dates as the wire carries them (AC42), from the picker's `YYYY-MM`. */
export function claimedDates(answer: DogAnswer): { validUntil: string | null; kcUntil: string | null } {
  return {
    validUntil: maskToVaccineDue(answer.validUntil) || null,
    kcUntil: maskToVaccineDue(answer.kcUntil) || null,
  };
}

export interface RegistrationDogsProps {
  me: PortalMe;
  answers: DogAnswers;
  onChange: (answers: DogAnswers) => void;
  onSubmit: () => void;
  onBack: () => void;
  /** The customer tapped a folded card open. The parent clears any dog it was pointing at, so a second refusal on the same dog can point again. */
  onOpen?: (dogId: string) => void;
  busy: boolean;
  /** The dog whose card is on its way up right now (AC23). */
  sending?: string | null;
  error: StepError | null;
  today: string;
  /** Which card starts open; the first dog by default. */
  open?: string;
}

const ACCEPT = 'image/jpeg,image/png,image/heic,image/heif,application/pdf';

/** The server refuses instructions longer than this, so the field cannot type past it. */
export const MEDS_MAX = 500;

/** The server refuses a longer name or breed, so the fields cannot type past it. */
export const NAME_MAX = 60;

export function VaxRow({
  label,
  name,
  value,
  editable,
  today,
  required = false,
  onChange,
}: {
  label: string;
  name: string;
  /** The claimed date as `YYYY-MM` when editable; else the desk's `"MAR 27"`. */
  value: string;
  editable: boolean;
  today: string;
  /**
   * A CARD IS IN PLAY, SO THIS DATE IS MANDATORY (Freddi, 2026-09-17).
   *
   * The submit refuses without it either way; this is so the customer learns it
   * from the field rather than from a refusal after tapping Finish. It appears
   * the moment a photo is chosen, which is also the moment it becomes true —
   * and that timing is the explanation: the card is why.
   */
  required?: boolean;
  onChange: (v: string) => void;
}) {
  const wire = editable ? maskToVaccineDue(value) : value;
  // A HALF-TYPED MONTH IS NEITHER VALID NOR AN ERROR. While they are still
  // typing, the row shows no verdict at all rather than flashing a red cross
  // at somebody mid-keystroke.
  const typing = editable && value !== '' && !maskComplete(value);
  const mark: VaxMark = typing ? 'off' : vaxState(wire, today);
  const shown = monthYearLabel(wire);
  const title = mark === 'ok' ? 'In date' : mark === 'bad' ? 'Out of date' : 'Date needed';
  // Only while it is actually outstanding. A filled field has nothing to ask for.
  const asking = required && editable && !maskComplete(value);
  return (
    <div className={'cp-vaxrow' + (mark === 'bad' ? ' is-bad' : '')}>
      <span className="nm">
        {label}
        {mark === 'bad' ? <small>Out of date</small> : asking && <small className="req">Required</small>}
      </span>
      {editable ? (
        <input
          // TEXT, NOT `month` AND NOT `number`. The month picker draws blank on
          // iOS and takes YYYY-MM, which is not what is printed on the card;
          // the number type strips the leading zero off "03". This is the card
          // expiry pattern: digits, the slash placed for them, and the numeric
          // keypad on both platforms.
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="MM/YY"
          // NO maxLength. A browser that enforces it while PASTING handed the
          // mask `03/20` out of `03/2027` — the six-digit correction below in
          // `maskMonthYear` never saw the paste (PR #228 review, codex
          // 2026-09-21). The mask is the length rule, and it is one anyway.
          className="cp-input cp-month"
          name={name}
          value={value}
          onChange={(e) => onChange(maskMonthYear(e.target.value, value))}
          aria-required={required || undefined}
          aria-label={`${label} — month and year, two digits each`}
        />
      ) : (
        <div className="cp-input is-static">{shown ?? <span className="ph">Month · year</span>}</div>
      )}
      {/* NOTHING UNTIL THERE IS SOMETHING TO SAY (Freddi, 2026-09-14). An empty
          bordered box read as a checkbox waiting to be ticked, which is not what
          it is — it is a verdict on a date that has not been given yet. The slot
          keeps its width so the row does not jump when the answer arrives. */}
      <span className={'vmark ' + mark} title={mark === 'off' ? undefined : title} aria-hidden={mark === 'off' || undefined}>
        {mark === 'ok' && G.tick({ size: 14, strokeWidth: 2.5 })}
        {mark === 'bad' && G.cross({ size: 14, strokeWidth: 2.5 })}
      </span>
    </div>
  );
}

/**
 * THE CARD THEY JUST PICKED, not a drawing of one (Freddi, 2026-09-14).
 *
 * A photo is the overwhelming case, and seeing it is how somebody knows they
 * attached the right one — the paper-lines glyph told them only that A file
 * exists. Made during render and revoked when it changes or the card unmounts,
 * so a customer swapping cards half a dozen times leaks nothing.
 *
 * TWO CASES STILL GET THE GLYPH, and both are honest: a PDF, which the browser
 * cannot thumbnail without a renderer, and a card already on file, whose bytes
 * live in R2 behind a route that does not exist yet — serving a stored proof
 * belongs to `vaccination-cards` (row 9). Neither is invented here.
 */
function useFilePreview(file: File | null): string | null {
  const url = useMemo(() => {
    if (!file || !file.type.startsWith('image/')) return null;
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
    return URL.createObjectURL(file);
  }, [file]);
  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);
  return url;
}

function Thumb({ file }: { file: File | null }) {
  const url = useFilePreview(file);
  return <span className="cp-thumb">{url && <img src={url} alt="" />}</span>;
}

function UploadWell({
  dog,
  answer,
  sending,
  onFile,
}: {
  dog: PortalDog;
  answer: DogAnswer;
  sending: boolean;
  onFile: (file: File | null, problem: string | null) => void;
}) {
  const inputId = `card-${dog.id}`;
  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    const problem = fileProblem(f);
    onFile(problem ? null : f, problem);
    e.target.value = '';
  };
  const input = <input id={inputId} type="file" accept={ACCEPT} onChange={pick} hidden />;

  if (sending && answer.file) {
    // Busy: the same row, no control — the file is on its way (AC23).
    return (
      <div className="cp-upload dc-has up-busy" aria-busy="true">
        <Thumb file={answer.file} />
        <div className="dc-file">
          <b>{answer.file.name}</b>
          <small>Sending…</small>
        </div>
      </div>
    );
  }
  if (answer.file) {
    // Attached: thumbnail, name over size · added just now, one Change (AC21).
    return (
      <label className={'cp-upload dc-has up-has' + (sending ? ' is-busy' : '')} aria-disabled={sending || undefined} htmlFor={inputId}>
        <Thumb file={answer.file} />
        <div className="dc-file">
          <b>{answer.file.name}</b>
          <small>{fileSize(answer.file.size)} · added just now</small>
        </div>
        <span className="dc-swap">
          {G.camera({ size: 13 })}Change
        </span>
        {input}
      </label>
    );
  }
  const bad = answer.fileError !== null;
  // A REFUSED CARD CAN BE LET GO OF (PR #228 review, codex 2026-09-21). The
  // refusal left `fileError` set, the step refuses to submit while it is, and
  // the well offered only another picker — so a customer whose one photo was
  // too big could not abandon the OPTIONAL card and finish (AC27). One control,
  // only while refused: back to no card, or back to the card already on file.
  const clear = bad && (
    <button type="button" className="cp-upload-clear" disabled={sending} onClick={() => onFile(null, null)}>
      {dog.card.state === 'added' ? 'Keep the card on file' : 'Carry on without a card'}
    </button>
  );
  if (dog.card.state === 'added') {
    // A card already on file from before — the well says so; Change swaps it.
    //
    // AND A REFUSED REPLACEMENT SAYS SO HERE. A bad file leaves `file` null, so
    // this branch drew *Added* exactly as before and the refusal never
    // appeared — the customer tapped Change, picked a 20 MB HEIC, and watched
    // nothing happen (issue 10, 2026-09-15). The card on file is still the
    // card on file; what changed is that the well now admits the swap failed.
    return (
      <>
      <label className={'cp-upload dc-has up-has' + (bad ? ' up-bad' : '') + (sending ? ' is-busy' : '')}
      aria-disabled={sending || undefined} htmlFor={inputId}>
        <Thumb file={null} />
        <div className="dc-file">
          <b>{bad ? 'That card wasn’t accepted' : 'Vaccination card'}</b>
          <small>{bad ? answer.fileError : 'Added'}</small>
        </div>
        <span className="dc-swap">
          {G.camera({ size: 13 })}Change
        </span>
        {input}
      </label>
      {clear}
      </>
    );
  }
  // Empty, or refused: a mark, the words, one Choose (AC20, AC22).
  return (
    <>
    <label className={'cp-upload dc-drop' + (bad ? ' up-bad' : '') + (sending ? ' is-busy' : '')}
      aria-disabled={sending || undefined} htmlFor={inputId}>
      <span className="dc-drop-ic">{bad ? G.warn({ size: 17 }) : G.camera({ size: 17 })}</span>
      <div className="dc-file">
        <b>{bad ? 'That card wasn’t accepted' : 'Add the card'}</b>
        <small>{bad ? answer.fileError : 'A photo or a PDF'}</small>
      </div>
      <span className="mp-btn quiet sm">Choose</span>
      {input}
    </label>
    {clear}
    </>
  );
}

function ShutCard({ dog, answer, index, count, onOpen, today }: { dog: PortalDog; answer: DogAnswer | undefined; index: number; count: number; onOpen: () => void; today: string }) {
  // A folded dog whose card still needs something wears the waiting tone, as
  // the prototype draws Paddy on `c2e`.
  //
  // BOTH DATES DECIDE IT. It checked only that a kennel-cough date EXISTED and
  // never what state it was in, so an expired one wore the neutral tone
  // whenever the annual was fine (P2.28, 2026-09-15). The dates come from the
  // ANSWER where the customer has typed one — a folded card was reading the
  // original API object, so opening another dog made completed edits look like
  // they had been lost (P2.27).
  const annual = maskToVaccineDue(answer?.validUntil ?? '') || dog.card.validUntil;
  const kc = maskToVaccineDue(answer?.kcUntil ?? '') || dog.card.kcUntil;
  const needs =
    dog.card.state !== 'added' ||
    !kc ||
    vaxState(annual, today) !== 'ok' ||
    vaxState(kc, today) !== 'ok';
  return (
    <button type="button" className="cp-dogcard shut" onClick={onOpen} aria-expanded="false">
      <div className="dc-head">
        <Avatar name={answer?.name || dog.name} id={dog.id} size={34} kind="dog" initials={initialOf(answer?.name || dog.name)} />
        <div className="dc-id">
          <b>{answer?.name || dog.name}</b>
          <small>{dogLine(dog, answer)}</small>
        </div>
        <Tag tone={needs ? 'wait' : 'neutral'}>
          {index} of {count}
        </Tag>
        <span className="dc-chev">{G.chevron({ size: 15 })}</span>
      </div>
    </button>
  );
}

function OpenCard({
  dog,
  index,
  count,
  answer,
  sending,
  busy,
  today,
  onChange,
}: {
  dog: PortalDog;
  index: number;
  count: number;
  answer: DogAnswer;
  sending: boolean;
  /**
   * THE WHOLE SUBMIT IS IN FLIGHT, not just this dog's upload.
   *
   * Every control here stayed live while the sequential uploads and then the
   * final POST ran, and all of it was read BEFORE those awaits: a date edited
   * mid-upload went up as the old one, a name or medication changed during the
   * final request never reached the payload, and a file chosen for a dog the
   * loop had already passed was never sent. Registration is once-only, so each
   * of those was permanent and none of them said a word (P1.14, P2.23,
   * 2026-09-15). The honest fix is that the form stops taking input the moment
   * it starts sending.
   */
  busy: boolean;
  today: string;
  onChange: (a: DogAnswer) => void;
}) {
  // ONE GATE FOR EVERY CONTROL ON THIS CARD. The inputs, the segments and the
  // file picker all go through here, so refusing while `busy` closes the whole
  // family of in-flight-edit losses at once rather than one control at a time.
  const set = (patch: Partial<DogAnswer>) => {
    if (busy) return;
    onChange({ ...answer, ...patch });
  };
  // ONE DEFINITION OF *a card is in play*, shared with the submit rule that
  // depends on it — chosen on this phone, or already on file from an earlier
  // sitting. Two copies of this drifting apart would mean a field that says
  // Required and a submit that does not ask, or worse, the other way round.
  const cardAttached = hasCard(dog, answer);
  // THE DATES ARE ALWAYS OPEN (Freddi, 2026-09-16).
  //
  // They were gated on a file being attached, and that gate was RIGHT at the
  // time: the only request carrying a date was the card upload, so a date typed
  // without one went nowhere and the customer read the old value back off their
  // own receipt with nothing having failed (Codex, PR #228).
  //
  // What changed is the plumbing, not the judgement. The registration POST
  // carries the dates now, so a typed date lands whether or not a photo comes
  // with it — and plenty of households have no card to photograph, where a
  // kennel with no dates at all is the worse outcome. The proof still records
  // the claim as evidence; the claim itself no longer depends on it.
  const datesEditable = true;
  const segId = `seg-${dog.id}`;
  return (
    <div className="cp-dogcard" aria-expanded="true">
      <div className="dc-head">
        <Avatar name={dog.name} id={dog.id} size={40} kind="dog" initials={initialOf(dog.name)} />
        <div className="dc-id">
          <b>{dog.name}</b>
          <small>{dogLine(dog)}</small>
        </div>
        <Tag tone="neutral">
          {index} of {count}
        </Tag>
        <span className="dc-chev is-open">{G.chevron({ size: 15 })}</span>
      </div>

      {/* PREFILLED AND EDITABLE (Freddi, 2026-09-14). These were the desk's
          facts, shown and not editable, on the reasoning that the customer's
          app only ever collects a FIRST fill (decisions 82, 157). That holds
          for a flag the desk set deliberately; it does not hold for a
          misspelled name, which the owner is simply better placed to know than
          whoever typed it at the desk. The name is required — a dog without
          one cannot be called for — and the breed may be left blank. */}
      <div className="dc-two">
        <div className="cp-field">
          <label className="ico" htmlFor={`dog-name-${dog.id}`}>{G.paw({ size: 14 })}Name</label>
          <input
            id={`dog-name-${dog.id}`}
            className={'cp-input' + (answer.name.trim() ? '' : ' is-bad')}
            name={`dog-name-${dog.id}`}
            value={answer.name}
            maxLength={NAME_MAX}
            autoComplete="off"
            aria-invalid={answer.name.trim() ? undefined : 'true'}
            disabled={busy}
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>
        <div className="cp-field">
          <label className="ico" htmlFor={`dog-breed-${dog.id}`}>{G.breed({ size: 14 })}Breed</label>
          <input
            id={`dog-breed-${dog.id}`}
            className="cp-input"
            name={`dog-breed-${dog.id}`}
            value={answer.breed}
            maxLength={NAME_MAX}
            placeholder="Breed"
            autoComplete="off"
            disabled={busy}
            onChange={(e) => set({ breed: e.target.value })}
          />
        </div>
      </div>

      <div className="dc-two">
        <div className="cp-field">
          <span className="ico" id={`${segId}-meds`}>
            {G.pill({ size: 14 })}Medication
          </span>
          <div className="cp-seg" role="radiogroup" aria-labelledby={`${segId}-meds`}>
            <button type="button" role="radio" disabled={busy} aria-checked={answer.meds === 'none'} className={answer.meds === 'none' ? 'on' : ''} onClick={() => set({ meds: 'none' })}>
              None
            </button>
            <button type="button" role="radio" disabled={busy} aria-checked={answer.meds === 'yes'} className={answer.meds === 'yes' ? 'on' : ''} onClick={() => set({ meds: 'yes' })}>
              Yes
            </button>
          </div>
        </div>
        <div className="cp-field">
          <span className="ico" id={`${segId}-mix`}>
            {G.dogs({ size: 14 })}With other dogs
          </span>
          <div className="cp-seg" role="radiogroup" aria-labelledby={`${segId}-mix`}>
            <button type="button" role="radio" disabled={busy} aria-checked={answer.apart === 'mixes'} className={answer.apart === 'mixes' ? 'on' : ''} onClick={() => set({ apart: 'mixes' })}>
              Mixes well
            </button>
            <button type="button" role="radio" disabled={busy} aria-checked={answer.apart === 'apart'} className={answer.apart === 'apart' ? 'on' : ''} onClick={() => set({ apart: 'apart' })}>
              Keep apart
            </button>
          </div>
        </div>
      </div>

      {answer.meds === 'yes' && (
        <div className="cp-field dc-instr">
          <label className="ico" htmlFor={`instructions-${dog.id}`}>
            {G.clipboard({ size: 14 })}Instructions
          </label>
          <input
            id={`instructions-${dog.id}`}
            className="cp-input"
            name={`instructions-${dog.id}`}
            value={answer.instructions}
            maxLength={MEDS_MAX}
            placeholder="What, how much, and when"
            disabled={busy}
            onChange={(e) => set({ instructions: e.target.value })}
          />
        </div>
      )}

      <div className="dc-vax">
        <div className="dc-vax-h">
          <span className="dc-vax-ic">{G.syringe({ size: 14 })}</span>
          <b>Vaccinations</b>
          {cardAttached ? <Tag tone="ok">Card added</Tag> : <Tag tone="neutral">Card not added</Tag>}
        </div>
        {/* A NEW CARD IS NEW EVIDENCE — BUT ONLY OF WHAT THE OLD CARD SAID.
            The dates were seeded from the card already on file and picking a
            replacement left them sitting there, so the new photo went up
            carrying the OLD card's claims; the two most often differ, which is
            the whole reason for replacing it (P2.24, 2026-09-15).

            That cleared BOTH fields whatever was in them, which threw away
            Patricia's own typing on any dog that had no card at all (Freddi,
            2026-09-19). Her record is not a stale claim — it is the best thing
            we hold — and a customer who attached a photo and then removed it
            lost her dates for good with nothing on screen to say so.
            `datesOnNewCard` drops the card's claim per field and keeps
            everything else. A refusal still leaves them alone; nothing changed. */}
        {/* BUSY IS NOT THE SAME AS SENDING, and the well only knew one.
            `sending` is this dog's own card going up; `busy` is the whole step
            submitting, during which `set()` refuses every edit — so the picker
            did nothing and looked exactly as it always does (PR #228 review). */}
        <UploadWell
          dog={dog}
          answer={answer}
          sending={sending || busy}
          onFile={(file, problem) =>
            set(
              file && !problem
                ? { file, fileError: null, ...datesOnNewCard(dog, answer) }
                : { file, fileError: problem },
            )
          }
        />
        <VaxRow
          required={cardAttached}
          label="Annual vaccination"
          name={`valid-${dog.id}`}
          // ONCE A CARD IS ON FILE IT SPEAKS FOR ITSELF. With no card at all the
          // desk's own date is what there is to show, and AC24 asks for it. But
          // a STORED card missing one of its dates fell back to the desk's too,
          // merging two deliberately separate sources and contradicting the
          // receipt, which reads only the proof (P2.25, 2026-09-15). A card
          // that claimed no annual date now shows none.
          value={
            datesEditable
              ? answer.validUntil
              : dog.card.state === 'added'
                ? (dog.card.validUntil ?? '')
                : (dog.vaccinesDue ?? '')
          }
          editable={datesEditable}
          today={today}
          onChange={(v) => set({ validUntil: v })}
        />
        <VaxRow
          required={cardAttached}
          label="Kennel cough"
          name={`kc-${dog.id}`}
          value={
            datesEditable
              ? answer.kcUntil
              : dog.card.state === 'added'
                ? (dog.card.kcUntil ?? '')
                : (dog.kcDue ?? '')
          }
          editable={datesEditable}
          today={today}
          onChange={(v) => set({ kcUntil: v })}
        />
      </div>
    </div>
  );
}

export function RegistrationDogs({ me, answers, onChange, onSubmit, onBack, onOpen, busy, sending = null, error, today, open }: RegistrationDogsProps) {
  const [openId, setOpenId] = useState<string | undefined>(open ?? me.dogs[0]?.id);
  /* THE STEP CAN POINT AT A DOG, AND THIS HAS TO LISTEN (PR #228 review).
     `open` seeded the state and nothing more, so a blocked submit naming a dog
     whose card is folded left the customer told to fix something they cannot
     see. It only ever changes when the submit refuses, so following it cannot
     fight the customer's own tapping.

     AND IT HAS TO BE ABLE TO POINT AT THE SAME DOG TWICE (PR #228 review,
     2026-10-06). The effect runs on a CHANGE of `open`; a second refusal naming
     the dog it named last time is no change, so a customer who had since
     opened another card was left hunting again. `onOpen` tells the parent the
     customer moved, the parent lets go of the dog it was pointing at, and the
     next refusal is a change again. */
  useEffect(() => {
    if (open) setOpenId(open);
  }, [open]);
  const count = me.dogs.length;

  return (
    <StepPage onSubmit={onSubmit}>
      <StepBar step={2} onBack={onBack} busy={busy} />
      <h1 className="cp-h1">Your dogs</h1>
      <p className="cp-p cp-lead">Tell us about your dogs.</p>

      <div className="cp-list">
        {me.dogs.map((dog, i) => {
          const answer = answers[dog.id];
          if (!answer) return null;
          return dog.id === openId ? (
            <OpenCard
              key={dog.id}
              dog={dog}
              index={i + 1}
              count={count}
              answer={answer}
              sending={sending === dog.id}
              busy={busy}
              today={today}
              onChange={(a) => onChange({ ...answers, [dog.id]: a })}
            />
          ) : (
            <ShutCard key={dog.id} dog={dog} answer={answers[dog.id]} index={i + 1} count={count} today={today} onOpen={() => { setOpenId(dog.id); onOpen?.(dog.id); }} />
          );
        })}
      </div>

      <p className="cp-p mute cp-foot-line">
        A new dog?{' '}
        <a className="wa-inline" href={whatsappHref('I have a new dog to add — ')} target="_blank" rel="noopener noreferrer">
          <WhatsAppMark size={13} />
          <u>WhatsApp Patricia</u>
        </a>{' '}
        and she’ll add them
      </p>

      <StepFailure error={error} />
      <div className="cp-actions">
        <PrimarySubmit busy={busy} icon={G.tick({ size: 15 })}>
          Submit your dogs
        </PrimarySubmit>
      </div>
    </StepPage>
  );
}
